/**
 * useBuilderForm Hook
 * Centraliserad state management och logik för byggföretagsformulär
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useCrudOperations } from '../../../components/Hooks/useCrudOperations';
import { API_URL, getAvatarUrl } from '../../../utils/api';
import { invalidateGlobalCache } from '../../../hooks/useApiCache';
import { 
  prepareBuilderDataForCreate, 
  prepareBuilderDataForUpdate, 
  convertToFormDataIfNeeded,
  validateAvatarFile
} from '../domain/builderTransforms';

/**
 * Custom hook för byggföretagsformulär
 * @param {Object} options - Konfiguration
 * @param {Object} options.initialBuilder - Initial byggföretagsdata (för uppdatering)
 * @param {Array} options.users - Lista av användare
 * @param {Function} options.onClose - Callback när formuläret stängs
 * @param {Function} options.onSuccess - Callback vid lyckad operation
 * @param {Function} options.setBuilders - State setter för byggföretagslista
 * @param {Array} options.builders - Byggföretagslista
 * @param {boolean} options.isUpdate - Om det är uppdateringsläge
 * @returns {Object} Form state och handlers
 */
export const useBuilderForm = ({
  initialBuilder = null,
  users = [],
  onClose,
  onSuccess,
  setBuilders,
  builders,
  isUpdate = false
}) => {
  // Initial state
  const getInitialValues = useCallback(() => {
    if (isUpdate && initialBuilder) {
      return {
        companyName: initialBuilder.companyName || '',
        kortnamn: initialBuilder.kortnamn || '',
        organizationNumber: initialBuilder.organizationNumber || '',
        website: initialBuilder.website || '',
        instagram: initialBuilder.instagram || '',
        avatar: initialBuilder.avatar || '',
        contactPerson: initialBuilder.contactPerson?._id || '',
        additionalContactPersons: initialBuilder.additionalContactPersons?.map(person =>
          typeof person === 'string' ? person : person._id
        ) || [],
        puPriorityContact: initialBuilder.puPriorityContact?._id || '',
        removeAvatar: false,
        hourlyRateMin: initialBuilder.pricing?.hourlyRateMin || 650,
        hourlyRateMax: initialBuilder.pricing?.hourlyRateMax || 890,
        materialHandlingRate: initialBuilder.pricing?.materialHandlingRate || 570,
        yrkesgruppPricing: initialBuilder.yrkesgruppPricing || {},
        // Adress
        addressStreet: initialBuilder.address?.street || '',
        addressZipCode: initialBuilder.address?.zipCode || '',
        addressCity: initialBuilder.address?.city || '',
        // Försäkring
        insuranceCompany: initialBuilder.insuranceCompany || '',
        // Behörigheter
        certifications: initialBuilder.certifications || []
      };
    }

    return {
      companyName: '',
      kortnamn: '',
      organizationNumber: '',
      website: '',
      instagram: '',
      avatar: '',
      hourlyRateMin: 650,
      hourlyRateMax: 890,
      materialHandlingRate: 570,
      yrkesgruppPricing: {},
      // Adress
      addressStreet: '',
      addressZipCode: '',
      addressCity: '',
      // Försäkring
      insuranceCompany: '',
      // Behörigheter
      certifications: []
    };
  }, [initialBuilder, isUpdate]);

  const [values, setValues] = useState(getInitialValues());
  const [previewUrl, setPreviewUrl] = useState(
    isUpdate && initialBuilder?.avatar ? getAvatarUrl(initialBuilder.avatar) : ''
  );
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const { addItem, updateItem } = useCrudOperations(`${API_URL}/builders`, setBuilders, builders);

  // Uppdatera state när initialBuilder ändras (för UpdateBuilder)
  useEffect(() => {
    if (isUpdate && initialBuilder) {
      setValues(getInitialValues());
      setPreviewUrl(initialBuilder.avatar ? getAvatarUrl(initialBuilder.avatar) : '');
    }
  }, [initialBuilder, isUpdate, getInitialValues]);

  // Filtrerade användare för dropdown
  const connectedUsers = useMemo(() => {
    if (!isUpdate || !initialBuilder) return [];
    
    return users.filter(user => {
      return user.role === 'byggare' && (
        user.builder === initialBuilder._id || 
        user.builder?._id === initialBuilder._id ||
        user.builder === initialBuilder
      );
    });
  }, [users, initialBuilder, isUpdate]);

  /**
   * Hanterar ändringar i formulärfält
   */
  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setValues(prev => ({ ...prev, [name]: value }));
  }, []);

  /**
   * Hanterar avatar-uppladdning
   */
  const handleAvatarChange = useCallback((file) => {
    const validation = validateAvatarFile(file);
    
    if (!validation.isValid) {
      setErrors(prev => ({ ...prev, avatar: validation.error }));
      return;
    }
    
    setValues(prev => ({ ...prev, avatar: file, removeAvatar: false }));
    setPreviewUrl(URL.createObjectURL(file));
    setErrors(prev => ({ ...prev, avatar: '' }));
  }, []);

  /**
   * Hanterar borttagning av avatar
   */
  const handleRemoveAvatar = useCallback(() => {
    setPreviewUrl('');
    setValues(prev => ({ ...prev, avatar: '', removeAvatar: true }));
  }, []);

  /**
   * Hanterar uppdatering av kontaktperson
   */
  const handleSetContactPerson = useCallback(async (userId) => {
    if (!isUpdate || !initialBuilder) return;
    
    try {
      setIsLoading(true);
      const response = await fetch(`${API_URL}/builders/${initialBuilder._id}/contact-person`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ userId })
      });

      if (!response.ok) throw new Error('Kunde inte uppdatera kontaktperson');

      const updatedBuilder = await response.json();
      setBuilders(prev => prev.map(b => 
        b._id === initialBuilder._id ? updatedBuilder : b
      ));
      
      setValues(prev => ({
        ...prev,
        contactPerson: userId
      }));
    } catch (error) {
      setErrorMessage('Kunde inte uppdatera kontaktperson');
      console.error('Error:', error);
    } finally {
      setIsLoading(false);
    }
  }, [isUpdate, initialBuilder, setBuilders]);

  /**
   * Hanterar uppdatering av PU-prioriterad kontaktperson
   */
  const handleSetPuPriorityContact = useCallback(async (userId) => {
    if (!isUpdate || !initialBuilder) return;
    
    try {
      setIsLoading(true);
      const response = await fetch(`${API_URL}/builders/${initialBuilder._id}/pu-priority-contact`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ userId })
      });

      if (!response.ok) throw new Error('Kunde inte uppdatera PU-prioriterad kontaktperson');

      const updatedBuilder = await response.json();
      setBuilders(prev => prev.map(b => 
        b._id === initialBuilder._id ? updatedBuilder : b
      ));
      
      setValues(prev => ({
        ...prev,
        puPriorityContact: userId
      }));
    } catch (error) {
      setErrorMessage('Kunde inte uppdatera PU-prioriterad kontaktperson');
      console.error('Error:', error);
    } finally {
      setIsLoading(false);
    }
  }, [isUpdate, initialBuilder, setBuilders]);

  /**
   * Invaliderar relevanta caches efter CRUD-operation
   */
  const invalidateCaches = useCallback(() => {
    invalidateGlobalCache([
      'dashboard_builders',
      'myassigned_builders',
      'dashboard_projects',
      'myassigned_projects',
      'myprojects_projects'
    ]);
  }, []);

  /**
   * Hanterar formulärsubmit
   */
  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMessage('');
    setErrors({});

    try {
      let builderData;
      
      // Förbered data baserat på operation
      if (isUpdate) {
        builderData = prepareBuilderDataForUpdate(values);
      } else {
        builderData = prepareBuilderDataForCreate(values);
      }
      
      // Lägg till avatar om det finns
      if (values.avatar instanceof File) {
        builderData.avatar = values.avatar;
      }
      
      // Konvertera till FormData om behövs (skicka med original certifications för pending files)
      const { body, isFormData } = convertToFormDataIfNeeded(builderData, isUpdate, values.certifications);
      
      // Utför operation
      if (isUpdate) {
        await updateItem(initialBuilder._id, body, isFormData);
      } else {
        await addItem(body, isFormData);
      }
      
      // Invalidera caches
      invalidateCaches();
      
      // Stäng formulär och kalla callbacks
      onClose();
      if (onSuccess) {
        onSuccess();
      }
      
    } catch (error) {
      console.error('Form submission error:', error);
      setErrorMessage(isUpdate ? 'Kunde inte uppdatera byggföretaget' : 'Kunde inte lägga till byggföretaget');
      setErrors(prev => ({
        ...prev,
        submit: isUpdate ? 'Kunde inte uppdatera byggföretaget' : 'Kunde inte lägga till byggföretaget'
      }));
    } finally {
      setIsLoading(false);
    }
  }, [values, isUpdate, initialBuilder, addItem, updateItem, invalidateCaches, onClose, onSuccess]);

  return {
    // State
    values,
    setValues,
    previewUrl,
    errors,
    isLoading,
    errorMessage,
    connectedUsers,
    
    // Handlers
    handleChange,
    handleAvatarChange,
    handleRemoveAvatar,
    handleSetContactPerson,
    handleSetPuPriorityContact,
    handleSubmit
  };
};

